import {
  createAsyncThunk,
  createSlice,
  isAnyOf,
} from "@reduxjs/toolkit";
import { triplePSDatasource } from "../../datasource";
import {
  APIResult,
  emptyAPIResult,
  loadingAPIResult,
  successAPIResult,
  errorAPIResult,
} from "../../lib/redux";
import {
  LatePunchoutThresholdMinutes,
  TurboCooldownThresholdMinutes,
} from "../../models";
import { logout } from "../Auth/authSlice";

export interface PayrollState {
  getLatePunchoutThreshold: APIResult<LatePunchoutThresholdMinutes>;
  updateLatePunchoutThreshold: APIResult<LatePunchoutThresholdMinutes>;
  latePunchoutThresholdMinutes?: LatePunchoutThresholdMinutes;
  getTurboCooldownThreshold: APIResult<TurboCooldownThresholdMinutes>;
  updateTurboCooldownThreshold: APIResult<TurboCooldownThresholdMinutes>;
  turboCooldownThresholdMinutes?: TurboCooldownThresholdMinutes;
}

const initialState: PayrollState = {
  getLatePunchoutThreshold: emptyAPIResult(),
  updateLatePunchoutThreshold: emptyAPIResult(),
  getTurboCooldownThreshold: emptyAPIResult(),
  updateTurboCooldownThreshold: emptyAPIResult(),
};

export const getLatePunchoutThreshold = createAsyncThunk(
  "payroll/getLatePunchoutThreshold",
  async () => {
    return triplePSDatasource.getLatePunchoutThreshold();
  }
);

export const updateLatePunchoutThreshold = createAsyncThunk(
  "payroll/updateLatePunchoutThreshold",
  async (minutes: LatePunchoutThresholdMinutes, thunkAPI) => {
    const updated = await triplePSDatasource.updateLatePunchoutThreshold(minutes);
    // Refetch so the form reflects server truth after the upsert.
    await thunkAPI.dispatch(getLatePunchoutThreshold());
    return updated;
  }
);

export const getTurboCooldownThreshold = createAsyncThunk(
  "payroll/getTurboCooldownThreshold",
  async () => {
    return triplePSDatasource.getTurboCooldownThreshold();
  }
);

export const updateTurboCooldownThreshold = createAsyncThunk(
  "payroll/updateTurboCooldownThreshold",
  async (minutes: TurboCooldownThresholdMinutes, thunkAPI) => {
    const updated = await triplePSDatasource.updateTurboCooldownThreshold(minutes);
    // Refetch so the form reflects server truth after the upsert.
    await thunkAPI.dispatch(getTurboCooldownThreshold());
    return updated;
  }
);

export const payrollSlice = createSlice({
  name: "payroll",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getLatePunchoutThreshold.pending, (state) => {
        state.getLatePunchoutThreshold = loadingAPIResult();
      })
      .addCase(getLatePunchoutThreshold.fulfilled, (state, action) => {
        state.getLatePunchoutThreshold = successAPIResult(action.payload);
        state.latePunchoutThresholdMinutes = action.payload;
      })
      .addCase(getLatePunchoutThreshold.rejected, (state, action) => {
        state.getLatePunchoutThreshold = errorAPIResult(action.error);
      })
      .addCase(updateLatePunchoutThreshold.pending, (state) => {
        state.updateLatePunchoutThreshold = loadingAPIResult();
      })
      .addCase(updateLatePunchoutThreshold.fulfilled, (state, action) => {
        state.updateLatePunchoutThreshold = successAPIResult(action.payload);
      })
      .addCase(updateLatePunchoutThreshold.rejected, (state, action) => {
        state.updateLatePunchoutThreshold = errorAPIResult(action.error);
      })
      .addCase(getTurboCooldownThreshold.pending, (state) => {
        state.getTurboCooldownThreshold = loadingAPIResult();
      })
      .addCase(getTurboCooldownThreshold.fulfilled, (state, action) => {
        state.getTurboCooldownThreshold = successAPIResult(action.payload);
        state.turboCooldownThresholdMinutes = action.payload;
      })
      .addCase(getTurboCooldownThreshold.rejected, (state, action) => {
        state.getTurboCooldownThreshold = errorAPIResult(action.error);
      })
      .addCase(updateTurboCooldownThreshold.pending, (state) => {
        state.updateTurboCooldownThreshold = loadingAPIResult();
      })
      .addCase(updateTurboCooldownThreshold.fulfilled, (state, action) => {
        state.updateTurboCooldownThreshold = successAPIResult(action.payload);
      })
      .addCase(updateTurboCooldownThreshold.rejected, (state, action) => {
        state.updateTurboCooldownThreshold = errorAPIResult(action.error);
      })
      .addMatcher(isAnyOf(logout), (state) => {
        state.getLatePunchoutThreshold = initialState.getLatePunchoutThreshold;
        state.updateLatePunchoutThreshold =
          initialState.updateLatePunchoutThreshold;
        state.latePunchoutThresholdMinutes = undefined;
        state.getTurboCooldownThreshold = initialState.getTurboCooldownThreshold;
        state.updateTurboCooldownThreshold =
          initialState.updateTurboCooldownThreshold;
        state.turboCooldownThresholdMinutes = undefined;
      });
  },
});

export default payrollSlice.reducer;
